import React, { useState, useEffect } from 'react';
import { Check, Loader2, CheckSquare, Clock, Users, FolderKanban, Target, Zap, BarChart3, Calendar } from 'lucide-react';

interface SetupStep {
  id: string;
  label: string;
  delay: number;
}

const SETUP_STEPS: SetupStep[] = [
  { id: 'account', label: 'Compte vérifié', delay: 700 },
  { id: 'security', label: 'Sécurisation de la session', delay: 1400 },
  { id: 'preferences', label: 'Chargement des préférences', delay: 2100 },
  { id: 'environment', label: 'Préparation de l\'environnement', delay: 2800 },
  { id: 'ready', label: 'Tout est prêt !', delay: 3600 },
];

export function WorkspaceSetupLoader() {
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string | null>(null);

  useEffect(() => {
    setCurrentStep(SETUP_STEPS[0].id);

    SETUP_STEPS.forEach((step, index) => {
      setTimeout(() => {
        setCompletedSteps(prev => new Set([...prev, step.id]));
        
        if (index < SETUP_STEPS.length - 1) {
          setCurrentStep(SETUP_STEPS[index + 1].id);
        } else {
          setCurrentStep(null);
        }
      }, step.delay);
    });
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 overflow-hidden">
      {/* Floating icons background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="floating-icon icon-1">
          <CheckSquare className="h-8 w-8 text-emerald-500/40" />
        </div>
        <div className="floating-icon icon-2">
          <Clock className="h-10 w-10 text-amber-500/40" />
        </div>
        <div className="floating-icon icon-3">
          <Users className="h-9 w-9 text-violet-500/40" />
        </div>
        <div className="floating-icon icon-4">
          <FolderKanban className="h-8 w-8 text-indigo-500/40" />
        </div>
        <div className="floating-icon icon-5">
          <Target className="h-7 w-7 text-rose-500/40" />
        </div>
        <div className="floating-icon icon-6">
          <Zap className="h-9 w-9 text-yellow-500/40" />
        </div>
        <div className="floating-icon icon-7">
          <BarChart3 className="h-8 w-8 text-cyan-500/40" />
        </div>
        <div className="floating-icon icon-8">
          <Calendar className="h-10 w-10 text-pink-500/40" />
        </div>
      </div>

      {/* Running rabbit */}
      <div className="relative mb-6 z-10">
        <div className="absolute bottom-0 left-1/2 h-1.5 w-48 -translate-x-1/2 bg-gradient-to-r from-transparent via-violet-400/60 to-transparent rounded-full" />
        
        <div className="rabbit-container">
          <svg viewBox="0 0 100 80" className="h-24 w-24">
            <defs>
              <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="50%" stopColor="#A855F7" />
                <stop offset="100%" stopColor="#D946EF" />
              </linearGradient>
              <linearGradient id="earGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="100%" stopColor="#C4B5FD" />
              </linearGradient>
              <linearGradient id="innerEarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F472B6" />
                <stop offset="100%" stopColor="#FB7185" />
              </linearGradient>
              <linearGradient id="bellyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#E9D5FF" />
                <stop offset="100%" stopColor="#F5D0FE" />
              </linearGradient>
              <radialGradient id="cheekGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FBCFE8" />
                <stop offset="100%" stopColor="#F9A8D4" stopOpacity="0" />
              </radialGradient>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <ellipse cx="50" cy="74" rx="18" ry="4" fill="rgba(0,0,0,0.15)" className="rabbit-shadow" />
            <ellipse cx="50" cy="55" rx="22" ry="16" fill="url(#bodyGrad)" className="rabbit-body" filter="url(#glow)" />
            <ellipse cx="52" cy="58" rx="12" ry="10" fill="url(#bellyGrad)" opacity="0.6" />
            <circle cx="72" cy="45" r="14" fill="url(#bodyGrad)" className="rabbit-head" filter="url(#glow)" />
            <ellipse cx="66" cy="23" rx="5" ry="14" fill="url(#earGrad)" className="rabbit-ear-left" />
            <ellipse cx="78" cy="21" rx="5" ry="16" fill="url(#earGrad)" className="rabbit-ear-right" />
            <ellipse cx="66" cy="23" rx="2.5" ry="9" fill="url(#innerEarGrad)" />
            <ellipse cx="78" cy="21" rx="2.5" ry="11" fill="url(#innerEarGrad)" />
            <ellipse cx="77" cy="42" rx="4" ry="4.5" fill="white" />
            <circle cx="77.5" cy="42" r="2.5" fill="#4C1D95" />
            <circle cx="78" cy="41.5" r="1.2" fill="#1e1b4b" />
            <circle cx="78.5" cy="40.5" r="0.8" fill="white" />
            <circle cx="76" cy="43" r="0.4" fill="white" />
            <ellipse cx="84" cy="47" rx="2.5" ry="2" fill="#EC4899" />
            <ellipse cx="84.5" cy="46.5" rx="0.8" ry="0.5" fill="#FBCFE8" opacity="0.8" />
            <ellipse cx="80" cy="50" rx="4" ry="2.5" fill="url(#cheekGrad)" />
            <line x1="85" y1="46" x2="93" y2="44" stroke="#D8B4FE" strokeWidth="0.5" opacity="0.6" />
            <line x1="85" y1="48" x2="94" y2="48" stroke="#D8B4FE" strokeWidth="0.5" opacity="0.6" />
            <line x1="85" y1="50" x2="93" y2="52" stroke="#D8B4FE" strokeWidth="0.5" opacity="0.6" />
            <circle cx="26" cy="52" r="8" fill="white" className="rabbit-tail" filter="url(#glow)" />
            <circle cx="24" cy="50" r="4" fill="#F3E8FF" />
            <circle cx="28" cy="54" r="3" fill="#FAE8FF" />
            <ellipse cx="67" cy="69" rx="5" ry="7" fill="url(#bodyGrad)" className="rabbit-front-leg" />
            <ellipse cx="36" cy="66" rx="7" ry="9" fill="url(#bodyGrad)" className="rabbit-back-leg" />
          </svg>
        </div>
        
        <div className="dust-particles">
          <span className="dust dust-1 bg-violet-400/70" />
          <span className="dust dust-2 bg-fuchsia-400/70" />
          <span className="dust dust-3 bg-pink-400/70" />
          <span className="dust dust-4 bg-purple-400/70" />
          <span className="dust dust-5 bg-indigo-400/70" />
        </div>
        
        <div className="speed-lines">
          <span className="speed-line line-1" />
          <span className="speed-line line-2" />
          <span className="speed-line line-3" />
        </div>
      </div>

      {/* Title */}
      <h2 className="text-xl font-semibold bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 bg-clip-text text-transparent mb-6 z-10">
        Préparation de votre espace de travail
      </h2>

      {/* Steps list */}
      <div className="flex flex-col gap-3 min-w-[280px] z-10">
        {SETUP_STEPS.map((step) => {
          const isCompleted = completedSteps.has(step.id);
          const isCurrent = currentStep === step.id;

          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-300 backdrop-blur-sm ${
                isCompleted 
                  ? 'bg-green-50/80 text-green-700 dark:bg-green-950/50 dark:text-green-400' 
                  : isCurrent 
                    ? 'bg-violet-50/80 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400' 
                    : 'bg-gray-50/80 text-gray-400 dark:bg-slate-800/50 dark:text-gray-500'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                {isCompleted ? (
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 animate-in zoom-in duration-200" />
                ) : isCurrent ? (
                  <Loader2 className="w-5 h-5 text-violet-600 dark:text-violet-400 animate-spin" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                )}
              </div>
              <span className={`text-sm font-medium transition-all duration-300 ${
                isCompleted || isCurrent ? 'opacity-100' : 'opacity-50'
              }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <style>{`
        .rabbit-container {
          animation: rabbit-hop 0.4s ease-in-out infinite;
        }
        
        .rabbit-shadow {
          animation: shadow-scale 0.4s ease-in-out infinite;
        }
        
        @keyframes shadow-scale {
          0%, 100% { transform: scale(1); opacity: 0.15; }
          50% { transform: scale(0.8); opacity: 0.1; }
        }
        
        @keyframes rabbit-hop {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-3deg); }
        }
        
        .rabbit-ear-left {
          transform-origin: 66px 37px;
          animation: ear-flop-left 0.4s ease-in-out infinite;
        }
        
        .rabbit-ear-right {
          transform-origin: 78px 37px;
          animation: ear-flop-right 0.4s ease-in-out infinite 0.05s;
        }
        
        @keyframes ear-flop-left {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-10deg); }
        }
        
        @keyframes ear-flop-right {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-12deg); }
        }
        
        .rabbit-front-leg {
          transform-origin: 67px 62px;
          animation: front-leg-run 0.2s ease-in-out infinite;
        }
        
        .rabbit-back-leg {
          transform-origin: 36px 57px;
          animation: back-leg-run 0.2s ease-in-out infinite 0.1s;
        }
        
        @keyframes front-leg-run {
          0%, 100% { transform: rotate(-25deg); }
          50% { transform: rotate(25deg); }
        }
        
        @keyframes back-leg-run {
          0%, 100% { transform: rotate(20deg); }
          50% { transform: rotate(-30deg); }
        }
        
        .rabbit-tail {
          animation: tail-wiggle 0.3s ease-in-out infinite;
        }
        
        @keyframes tail-wiggle {
          0%, 100% { transform: translateX(0) scale(1); }
          50% { transform: translateX(-3px) scale(1.1); }
        }
        
        .dust-particles {
          position: absolute;
          bottom: 10px;
          left: 5px;
        }
        
        .dust {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: dust-float 0.5s ease-out infinite;
        }
        
        .dust-1 { animation-delay: 0s; }
        .dust-2 { animation-delay: 0.1s; left: 8px; }
        .dust-3 { animation-delay: 0.2s; left: 16px; }
        .dust-4 { animation-delay: 0.3s; left: 24px; }
        .dust-5 { animation-delay: 0.4s; left: 32px; }
        
        @keyframes dust-float {
          0% { opacity: 0.9; transform: translateX(0) translateY(0) scale(1); }
          100% { opacity: 0; transform: translateX(-25px) translateY(-15px) scale(0.2); }
        }
        
        .speed-lines {
          position: absolute;
          top: 50%;
          left: -20px;
          transform: translateY(-50%);
        }
        
        .speed-line {
          position: absolute;
          height: 2px;
          border-radius: 1px;
          background: linear-gradient(to left, rgba(168, 85, 247, 0.7), transparent);
          animation: speed-line-move 0.3s ease-out infinite;
        }
        
        .line-1 { width: 20px; top: -8px; animation-delay: 0s; }
        .line-2 { width: 30px; top: 0; animation-delay: 0.1s; }
        .line-3 { width: 15px; top: 8px; animation-delay: 0.2s; }
        
        @keyframes speed-line-move {
          0% { opacity: 0; transform: translateX(10px); }
          50% { opacity: 0.8; }
          100% { opacity: 0; transform: translateX(-20px); }
        }
        
        .floating-icon {
          position: absolute;
          animation: float-around 8s ease-in-out infinite;
        }
        
        .icon-1 { top: 10%; left: 15%; animation-delay: 0s; }
        .icon-2 { top: 20%; right: 20%; animation-delay: 1s; }
        .icon-3 { top: 60%; left: 10%; animation-delay: 2s; }
        .icon-4 { top: 70%; right: 15%; animation-delay: 3s; }
        .icon-5 { top: 30%; left: 25%; animation-delay: 4s; }
        .icon-6 { top: 80%; left: 30%; animation-delay: 5s; }
        .icon-7 { top: 15%; right: 30%; animation-delay: 6s; }
        .icon-8 { top: 50%; right: 25%; animation-delay: 7s; }
        
        @keyframes float-around {
          0%, 100% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0.4; }
          25% { transform: translateY(-20px) rotate(10deg) scale(1.1); opacity: 0.6; }
          50% { transform: translateY(-10px) rotate(-5deg) scale(1); opacity: 0.5; }
          75% { transform: translateY(-25px) rotate(5deg) scale(1.05); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
